import { useEffect, useState } from 'react';
import axios from 'axios';
import { ShieldCheck, RefreshCw, CheckCircle2, XCircle, History } from 'lucide-react';

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

interface EntraIntegration {
  tenant_id: number;
  azure_tenant_id: string;
  client_id: string;
  enabled: boolean;
  has_secret: boolean;
  last_sync_at: string | null;
  last_sync_status: 'ok' | 'error' | null;
  last_error: string | null;
}

// Configurazione dell'integrazione Microsoft Entra ID / M365 per il tenant selezionato: app
// registration Azure AD (client-credentials flow) usata dal job periodico backend/src/jobs/
// entraSync.ts per enumerare i device come asset e segnalare i sign-in a rischio/falliti.
export default function EntraIntegrationPanel({ tenantId }: { tenantId: number | null }) {
  const [integration, setIntegration] = useState<EntraIntegration | null>(null);
  const [loading, setLoading] = useState(true);
  const [azureTenantId, setAzureTenantId] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [backfillSince, setBackfillSince] = useState(isoDate(new Date(Date.now() - 30 * 86400000)));
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState<{ ok: boolean; message: string } | null>(null);

  useEffect(() => {
    if (!tenantId) return;
    setLoading(true);
    setTestResult(null);
    axios.get('/api/entra-integration', { params: { tenantId } })
      .then(res => {
        const data: EntraIntegration | null = res.data.data;
        setIntegration(data);
        setAzureTenantId(data?.azure_tenant_id || '');
        setClientId(data?.client_id || '');
        setClientSecret('');
        setEnabled(data?.enabled || false);
      })
      .catch(err => setError(err.response?.data?.error || 'Error while loading the integration'))
      .finally(() => setLoading(false));
  }, [tenantId]);

  if (!tenantId) return null;

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const res = await axios.put('/api/entra-integration', {
        tenantId, azureTenantId, clientId, clientSecret: clientSecret || undefined, enabled,
      });
      setIntegration(prev => ({ ...(res.data.data), has_secret: true, last_error: prev?.last_error ?? null, last_sync_status: prev?.last_sync_status ?? null }));
      setClientSecret('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error while saving the integration');
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await axios.post(`/api/entra-integration/${tenantId}/test`);
      setTestResult({ ok: true, message: `Connected — ${res.data.data.deviceCount} device(s) visible in the directory.` });
    } catch (err: any) {
      setTestResult({ ok: false, message: err.response?.data?.error || 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  }

  async function handleBackfill() {
    setBackfilling(true);
    setBackfillResult(null);
    try {
      const res = await axios.post(`/api/entra-integration/${tenantId}/backfill`, { sinceDate: backfillSince });
      setBackfillResult({ ok: true, message: `Imported ${res.data.data.devices} device(s) and ${res.data.data.signIns} sign-in(s) since ${backfillSince}.` });
    } catch (err: any) {
      setBackfillResult({ ok: false, message: err.response?.data?.error || 'Backfill failed' });
    } finally {
      setBackfilling(false);
    }
  }

  return (
    <div className="card p-4 mb-4">
      <h2 className="font-medium text-sm text-gray-900 mb-1 inline-flex items-center gap-2">
        <ShieldCheck size={15} /> Microsoft Entra ID / M365 integration
      </h2>
      <p className="text-xs text-gray-400 mb-3">
        App-only (client-credentials) connection to Microsoft Graph: enumerates directory devices as
        assets and flags risky/failed sign-ins. Requires an Azure AD app registration with
        Device.Read.All (and AuditLog.Read.All + Directory.Read.All for sign-in monitoring, which
        needs Azure AD Premium).
      </p>
      {error && <div className="mb-3 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</div>}
      {loading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : (
        <form onSubmit={handleSave} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Azure Tenant ID</label>
            <input value={azureTenantId} onChange={e => setAzureTenantId(e.target.value)} required
              className="input-field font-mono text-xs" placeholder="00000000-0000-0000-0000-000000000000" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Client ID (App registration)</label>
            <input value={clientId} onChange={e => setClientId(e.target.value)} required
              className="input-field font-mono text-xs" placeholder="00000000-0000-0000-0000-000000000000" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">
              Client secret {integration?.has_secret && <span className="text-gray-400">(leave blank to keep the current one)</span>}
            </label>
            <input type="password" value={clientSecret} onChange={e => setClientSecret(e.target.value)}
              className="input-field font-mono text-xs" placeholder={integration?.has_secret ? '••••••••' : ''}
              required={!integration?.has_secret} />
          </div>
          <div className="md:col-span-3 flex items-center justify-between">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
              Enabled (syncs every 15 minutes)
            </label>
            <div className="flex items-center gap-2">
              {integration && (
                <button type="button" onClick={handleTest} disabled={testing} className="btn-secondary flex items-center gap-1 disabled:opacity-50">
                  <RefreshCw size={13} className={testing ? 'animate-spin' : ''} /> Test connection
                </button>
              )}
              <button type="submit" disabled={saving} className="btn-primary disabled:opacity-50">
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </form>
      )}

      {testResult && (
        <div className={`mt-3 text-sm rounded-lg px-3 py-2 inline-flex items-center gap-1.5 ${testResult.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
          {testResult.ok ? <CheckCircle2 size={14} /> : <XCircle size={14} />} {testResult.message}
        </div>
      )}

      {integration?.last_sync_at && (
        <p className="mt-3 text-xs text-gray-400">
          Last sync: {new Date(integration.last_sync_at).toLocaleString('en-GB')} —{' '}
          {integration.last_sync_status === 'ok'
            ? <span className="text-emerald-600">ok</span>
            : <span className="text-red-600" title={integration.last_error || ''}>error{integration.last_error ? `: ${integration.last_error}` : ''}</span>}
        </p>
      )}

      {integration && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <p className="text-xs font-medium text-gray-600 mb-1.5 inline-flex items-center gap-1.5">
            <History size={12} /> Backfill historical logs
          </p>
          <p className="text-xs text-gray-400 mb-2">
            One-off import of devices and sign-ins from a past date, in addition to the regular 15-minute
            sync. Does not affect the incremental sync schedule. Microsoft Graph's sign-in log itself only
            retains ~30 days of history without Entra ID Premium P2 + Log Analytics, regardless of the date
            chosen here.
          </p>
          <div className="flex items-center gap-2">
            <input type="date" value={backfillSince} onChange={e => setBackfillSince(e.target.value)}
              className="input-field py-1 px-2 text-xs w-auto" max={isoDate(new Date())} />
            <button type="button" onClick={handleBackfill} disabled={backfilling}
              className="btn-secondary flex items-center gap-1 disabled:opacity-50">
              <RefreshCw size={13} className={backfilling ? 'animate-spin' : ''} /> {backfilling ? 'Importing…' : 'Backfill'}
            </button>
          </div>
          {backfillResult && (
            <div className={`mt-2 text-sm rounded-lg px-3 py-2 inline-flex items-center gap-1.5 ${backfillResult.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
              {backfillResult.ok ? <CheckCircle2 size={14} /> : <XCircle size={14} />} {backfillResult.message}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
